import { useLanding } from '@/components/Landing/LandingKit';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Pressable, Platform, Animated as RNAnimated } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';

// ============================================================================
// CSS ANIMATION INJECTOR (runs once)
// ============================================================================
let stylesInjected = false;
const injectLearnStyles = () => {
  if (Platform.OS !== 'web' || stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.id = 'learn-components-styles';
  style.textContent = `
    @keyframes confettiBurst {
      0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
      100% { opacity: 0; }
    }
    @keyframes shakeX {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-6px); }
      40% { transform: translateX(6px); }
      60% { transform: translateX(-4px); }
      80% { transform: translateX(4px); }
    }
    @keyframes checkPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.3); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 4px rgba(13,92,71,0.3); }
      50% { box-shadow: 0 0 16px rgba(13,92,71,0.6); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmerBorder {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes celebrateBounce {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.1); }
      70% { transform: scale(0.95); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes starOrbit {
      0% { transform: rotate(0deg) translateX(40px) rotate(0deg); }
      100% { transform: rotate(360deg) translateX(40px) rotate(-360deg); }
    }
    @keyframes ctaGlow {
      0%, 100% { box-shadow: 0 0 8px rgba(13,92,71,0.3); }
      50% { box-shadow: 0 0 20px rgba(13,92,71,0.6); }
    }
    .learn-shake { animation: shakeX 0.5s ease-in-out; }
    .learn-check-pop { animation: checkPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
    .learn-pulse-glow { animation: pulseGlow 2s ease-in-out infinite; }
    .learn-celebrate-bounce { animation: celebrateBounce 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
    .learn-cta-glow { animation: ctaGlow 2s ease-in-out infinite; }
  `;
  if (!document.getElementById('learn-components-styles')) {
    document.head.appendChild(style);
  }
};

// ============================================================================
// MINI VOICE WAVE BARS (for mock composer)
// ============================================================================
const MiniVoiceWaveBars = () => {
  const barHeights = [7.5, 8.5, 10, 8.5, 7.5];
  return (
    <View style={{ position: 'absolute', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      {barHeights.map((h, i) => (
        <View key={i} style={{ width: 2.5, height: h, borderRadius: 1.5, backgroundColor: '#fff' }} />
      ))}
    </View>
  );
};

// ============================================================================
// MOCK COMPOSER - Read-only visual replica of Gab AI composer
// ============================================================================
export const MockComposer = ({
  prompt,
  label,
  caption,
  showAttachButton = true,
  showModelSelector = true,
  showVoiceButton = true,
  highlightParts = [],
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  useEffect(() => { injectLearnStyles(); }, []);

  // Render prompt text with optional highlights
  const renderPromptText = () => {
    if (!highlightParts || highlightParts.length === 0) {
      return (
        <Text
          size={isXS ? 's' : 'n'}
          style={{
            color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
            lineHeight: 22,
          }}
        >
          {prompt}
        </Text>
      );
    }

    // Build highlighted text
    let remaining = prompt;
    const parts = [];
    let key = 0;

    for (const highlight of highlightParts) {
      const idx = remaining.toLowerCase().indexOf(highlight.toLowerCase());
      if (idx === -1) continue;

      if (idx > 0) {
        parts.push(
          <Text key={key++} style={{ color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)' }}>
            {remaining.slice(0, idx)}
          </Text>
        );
      }
      parts.push(
        <Text
          key={key++}
          style={{
            color: '#0D5C47',
            backgroundColor: isDark ? 'rgba(13,92,71,0.15)' : 'rgba(13,92,71,0.1)',
            borderRadius: 4,
            paddingHorizontal: 2,
            fontWeight: '600',
          }}
        >
          {remaining.slice(idx, idx + highlight.length)}
        </Text>
      );
      remaining = remaining.slice(idx + highlight.length);
    }

    if (remaining) {
      parts.push(
        <Text key={key++} style={{ color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)' }}>
          {remaining}
        </Text>
      );
    }

    return (
      <Text size={isXS ? 's' : 'n'} style={{ lineHeight: 22 }}>
        {parts}
      </Text>
    );
  };

  return (
    <View style={{ marginBottom: 20 }}>
      {label && (
        <Text size="s" weight="semibold" style={{ marginBottom: 8, color: '#0D5C47' }}>
          {label}
        </Text>
      )}
      <View
        style={{
          width: '100%',
          borderRadius: isXS ? 14 : 20,
          borderWidth: 1,
          borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
          backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          overflow: 'hidden',
        }}
      >
        {/* Text area */}
        <View
          style={{
            minHeight: isXS ? 42 : 46,
            paddingVertical: isXS ? 12 : 14,
            paddingLeft: isXS ? 16 : 20,
            paddingRight: isXS ? 12 : 16,
          }}
        >
          {renderPromptText()}
        </View>

        {/* Actions Row */}
        <View
          style={{
            height: isXS ? 46 : 44,
            paddingHorizontal: 8,
            paddingBottom: 8,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {/* Left icons */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            {showAttachButton && (
              <View style={{ width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="paperclip" size={14} color={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'} />
              </View>
            )}
            <View style={{ width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="sliders-simple" size={14} color={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'} />
            </View>
          </View>

          {/* Right icons */}
          <View style={{ marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            {showModelSelector && (
              <View style={{ height: 32, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <Text size="s" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)' }}>Auto</Text>
                <Icon name="chevron-down" size={8} color={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'} />
              </View>
            )}
            <View
              style={{
                width: 32, height: 32, borderRadius: 16,
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: isDark ? '#2D2E2F' : '#f0f2f5',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <Icon name="microphone-lines" size={14} solid color={isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)'} />
            </View>
            {showVoiceButton && (
              <View
                style={{
                  width: 32, height: 32, borderRadius: 16,
                  alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #0A3F32, #0D5C47)',
                  backgroundColor: '#0D5C47',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <MiniVoiceWaveBars />
              </View>
            )}
            {/* Send button */}
            <View
              style={{
                width: 32, height: 32, borderRadius: 16,
                backgroundColor: prompt ? (isDark ? '#fff' : '#000') : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon
                name="arrow-up"
                size={14}
                color={prompt
                  ? (isDark ? '#000' : '#fff')
                  : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)')}
              />
            </View>
          </View>
        </View>
      </View>
      {caption && (
        <Text size="xs" color="secondary" style={{ marginTop: 8, lineHeight: 18, fontStyle: 'italic' }}>
          {caption}
        </Text>
      )}
    </View>
  );
};

// ============================================================================
// PROMPT EXAMPLE - Before/After Comparison with MockComposers
// ============================================================================
export const PromptExample = ({
  bad,
  good,
  badCaption,
  goodCaption,
}) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  return (
    <View style={{ marginBottom: 24 }}>
      {/* Bad prompt */}
      <View style={{ marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <View style={{
            width: 22, height: 22, borderRadius: 11,
            backgroundColor: isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="xmark" size={10} color="#EF4444" solid />
          </View>
          <Text size="s" weight="semibold" style={{ color: '#EF4444' }}>Weak Prompt</Text>
        </View>
        <MockComposer prompt={bad} caption={badCaption} />
      </View>

      {/* Divider arrow */}
      <View style={{ alignItems: 'center', marginBottom: 16 }}>
        <View style={{
          width: 32, height: 32, borderRadius: 16,
          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="arrow-down" size={14} color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} />
        </View>
      </View>

      {/* Good prompt */}
      <View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <View style={{
            width: 22, height: 22, borderRadius: 11,
            backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="check" size={10} color="#10B981" solid />
          </View>
          <Text size="s" weight="semibold" style={{ color: '#10B981' }}>Strong Prompt</Text>
        </View>
        <MockComposer prompt={good} caption={goodCaption} />
      </View>
    </View>
  );
};

// ============================================================================
// CONFETTI BURST - CSS particle celebration
// ============================================================================
export const ConfettiBurst = ({ active, originX = '50%', originY = '50%' }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!active || Platform.OS !== 'web') return;
    const colors = ['#0A3F32', '#0D5C47', '#178A68', '#1A9B74', '#6BB89A', '#0D5C47'];
    const newParticles = Array.from({ length: 24 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      size: 4 + Math.random() * 5,
      angle: (i / 24) * 360 + (Math.random() * 30 - 15),
      distance: 40 + Math.random() * 60,
      duration: 600 + Math.random() * 600,
      delay: Math.random() * 100,
      isCircle: Math.random() > 0.5,
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => setParticles([]), 1400);
    return () => clearTimeout(timer);
  }, [active]);

  if (particles.length === 0) return null;

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 10 }}>
      {particles.map(p => {
        const rad = (p.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * p.distance;
        const ty = Math.sin(rad) * p.distance;
        return (
          <View
            key={p.id}
            style={{
              position: 'absolute',
              left: originX,
              top: originY,
              width: p.size,
              height: p.size,
              borderRadius: p.isCircle ? p.size / 2 : 2,
              backgroundColor: p.color,
              animation: `confettiBurst ${p.duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${p.delay}ms forwards`,
              '--tx': `${tx}px`,
              '--ty': `${ty}px`,
              // Use inline keyframe override via transform
              animationName: 'none',
              transform: [{ translateX: 0 }, { translateY: 0 }],
              opacity: 0,
              // CSS override
              ...(Platform.OS === 'web' ? {
                animation: `${p.duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${p.delay}ms forwards`,
                animationName: `confetti-p-${p.id}`,
              } : {}),
            }}
          >
            {Platform.OS === 'web' && (
              <style>{`
                @keyframes confetti-p-${p.id} {
                  0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
                  100% { transform: translate(${tx}px, ${ty}px) rotate(${360 + Math.random() * 360}deg) scale(0.2); opacity: 0; }
                }
              `}</style>
            )}
          </View>
        );
      })}
    </View>
  );
};

// ============================================================================
// LESSON QUIZ - Animated knowledge check
// ============================================================================
function isLessonQuizQuestion(item) {
  return Boolean(
    item &&
    typeof item === 'object' &&
    !Array.isArray(item) &&
    String(item.question || '').trim() &&
    Array.isArray(item.options) &&
    item.options.length >= 2
  );
}

export const LessonQuiz = ({ questions = [] }) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [confettiIndex, setConfettiIndex] = useState(null);
  const quizQuestions = (Array.isArray(questions) ? questions : []).filter(isLessonQuizQuestion);

  useEffect(() => { injectLearnStyles(); }, []);

  if (quizQuestions.length === 0) return null;

  const handleAnswer = (qIndex, optionIndex) => {
    if (answers[qIndex] !== undefined) return; // already answered

    const isCorrect = quizQuestions[qIndex].correctIndex === optionIndex;
    setAnswers(prev => ({ ...prev, [qIndex]: optionIndex }));

    if (isCorrect) {
      setConfettiIndex(qIndex);
      setTimeout(() => setConfettiIndex(null), 1500);
    }

    // Check if all answered
    const newAnswers = { ...answers, [qIndex]: optionIndex };
    if (Object.keys(newAnswers).length === quizQuestions.length) {
      setTimeout(() => setShowResults(true), 800);
    }
  };

  const correctCount = Object.entries(answers).filter(
    ([qIdx, aIdx]) => quizQuestions[parseInt(qIdx, 10)]?.correctIndex === aIdx
  ).length;

  return (
    <View style={{
      marginTop: 32,
      marginBottom: 24,
      padding: isXS ? 20 : 28,
      borderRadius: 6,
      backgroundColor: isDark ? 'rgba(13,92,71,0.06)' : 'rgba(13,92,71,0.03)',
      borderWidth: 1,
      borderColor: isDark ? 'rgba(13,92,71,0.15)' : 'rgba(13,92,71,0.1)',
    }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <View style={{
          width: 36, height: 36, borderRadius: 10,
          backgroundColor: '#0D5C4720',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="circle-question" size={18} color="#0D5C47" solid />
        </View>
        <View>
          <Text size="l" weight="semibold">Knowledge Check</Text>
          <Text size="xs" color="secondary">Test what you've learned</Text>
        </View>
      </View>

      {/* Questions */}
      {quizQuestions.map((q, qIndex) => {
        const userAnswer = answers[qIndex];
        const isAnswered = userAnswer !== undefined;
        const isCorrect = userAnswer === q.correctIndex;

        return (
          <View
            key={qIndex}
            style={{
              marginBottom: 24,
              position: 'relative',
              animation: `fadeInUp 0.4s ease-out ${qIndex * 100}ms both`,
            }}
          >
            {confettiIndex === qIndex && (
              <ConfettiBurst active={true} originX="50%" originY="30%" />
            )}

            <Text size="m" weight="medium" style={{ marginBottom: 12 }}>
              {qIndex + 1}. {q.question}
            </Text>

            <View style={{ gap: 8 }}>
              {q.options.map((option, oIndex) => {
                const isSelected = userAnswer === oIndex;
                const isCorrectOption = q.correctIndex === oIndex;
                const showAsCorrect = isAnswered && isCorrectOption;
                const showAsWrong = isSelected && !isCorrect;

                return (
                  <Pressable
                    key={oIndex}
                    onPress={() => handleAnswer(qIndex, oIndex)}
                    className={showAsWrong ? 'learn-shake' : ''}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      paddingVertical: 12,
                      paddingHorizontal: 16,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: showAsCorrect
                        ? '#10B981'
                        : showAsWrong
                          ? '#EF4444'
                          : hovered && !isAnswered
                            ? (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)')
                            : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                      backgroundColor: showAsCorrect
                        ? (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)')
                        : showAsWrong
                          ? (isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)')
                          : hovered && !isAnswered
                            ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)')
                            : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'),
                      cursor: isAnswered ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                    })}
                  >
                    {/* Status indicator */}
                    <View style={{
                      width: 24, height: 24, borderRadius: 12,
                      borderWidth: 2,
                      borderColor: showAsCorrect ? '#10B981' : showAsWrong ? '#EF4444' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'),
                      alignItems: 'center', justifyContent: 'center',
                      backgroundColor: showAsCorrect ? '#10B981' : showAsWrong ? '#EF4444' : 'transparent',
                    }}>
                      {showAsCorrect && (
                        <View className="learn-check-pop">
                          <Icon name="check" size={12} color="#fff" solid />
                        </View>
                      )}
                      {showAsWrong && (
                        <Icon name="xmark" size={12} color="#fff" solid />
                      )}
                    </View>

                    <Text size="m" style={{
                      flex: 1,
                      color: showAsCorrect ? '#10B981' : showAsWrong ? '#EF4444' : colors.text_color_primary,
                      fontWeight: showAsCorrect ? '600' : '400',
                    }}>
                      {option}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {/* Explanation */}
            {isAnswered && q.explanation && (
              <View style={{
                marginTop: 8,
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 10,
                backgroundColor: isDark ? 'rgba(13,92,71,0.08)' : 'rgba(13,92,71,0.05)',
              }}>
                <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
                  {q.explanation}
                </Text>
              </View>
            )}
          </View>
        );
      })}

      {/* Results */}
      {showResults && (
        <LessonComplete
          score={correctCount}
          total={quizQuestions.length}
        />
      )}
    </View>
  );
};

// ============================================================================
// LESSON COMPLETE - Celebration overlay
// ============================================================================
export const LessonComplete = ({ score, total }) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    injectLearnStyles();
    const timer = setTimeout(() => setShowConfetti(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const isPerfect = score === total;
  const isGood = score >= total * 0.7;

  const message = isPerfect
    ? 'Perfect Score!'
    : isGood
      ? 'Great Job!'
      : 'Keep Learning!';

  const subtitle = isPerfect
    ? 'You nailed every question!'
    : isGood
      ? 'You\'re getting the hang of this!'
      : 'Review the lesson and try again.';

  return (
    <View style={{ position: 'relative', alignItems: 'center', paddingVertical: 24 }}>
      {showConfetti && isPerfect && (
        <ConfettiBurst active={true} originX="50%" originY="30%" />
      )}

      <View
        className="learn-celebrate-bounce"
        style={{
          alignItems: 'center',
          padding: isXS ? 24 : 32,
          borderRadius: 6,
          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          borderWidth: 1,
          borderColor: isPerfect
            ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.2)')
            : (isDark ? 'rgba(13,92,71,0.2)' : 'rgba(13,92,71,0.15)'),
          width: '100%',
        }}
      >
        {/* Stars */}
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
          {[...Array(3)].map((_, i) => (
            <Icon
              key={i}
              name="star"
              size={isXS ? 20 : 24}
              solid
              color={i < (isPerfect ? 3 : isGood ? 2 : 1) ? '#F59E0B' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')}
            />
          ))}
        </View>

        <Text size="xl" weight="bold" style={{ marginBottom: 4, textAlign: 'center' }}>
          {message}
        </Text>
        <Text size="m" color="secondary" style={{ marginBottom: 8, textAlign: 'center' }}>
          {subtitle}
        </Text>
        <Text size="l" weight="semibold" style={{ color: '#0D5C47' }}>
          {score}/{total} correct
        </Text>
      </View>
    </View>
  );
};

// ============================================================================
// PROGRESS BAR - Animated module progress
// ============================================================================
export const ProgressBar = ({ current, total }) => {
  const { c } = useLanding();
  return <View style={{ marginBottom: 28, gap: 12 }}>
    <Text style={{ color: c.muted, fontSize: 12 }}>Lesson {current} of {total}</Text>
    <View accessibilityLabel={`Lesson ${current} of ${total}`} style={{ flexDirection: 'row', gap: 5 }}>
      {Array.from({ length: total }, (_, index) => <View key={index} style={{ flex: 1, height: 2, backgroundColor: index + 1 === current ? c.accent : c.rule }} />)}
    </View>
  </View>;
};

// ============================================================================
// KEY TAKEAWAYS - Animated summary box
// ============================================================================
export const KeyTakeaways = ({ items = [] }) => {
  const { c } = useLanding();
  return <View style={{ marginVertical: 32, paddingVertical: 24, borderTopWidth: 1, borderBottomWidth: 1, borderColor: c.rule }}>
    <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 23, lineHeight: 30, marginBottom: 18 }}>Key takeaways</Text>
    {items.map((item, index) => <View key={index} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}><Icon name="check" size={12} color={c.accent} style={{ marginTop: 6 }} /><Text style={{ flex: 1, color: c.inkSoft, fontSize: 16, lineHeight: 26 }}>{item}</Text></View>)}
  </View>;
};

// ============================================================================
// TRY GAB CTA - Encourages sign up after lesson content
// ============================================================================
export const TryGabCTA = ({ lessonTopic }) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const router = useRouter();

  useEffect(() => { injectLearnStyles(); }, []);

  return (
    <View
      style={{
        marginTop: 40,
        marginBottom: 24,
        borderRadius: 6,
        overflow: 'hidden',
        position: 'relative',
        borderWidth: 1,
        borderColor: isDark ? 'rgba(13,92,71,0.2)' : 'rgba(13,92,71,0.15)',
      }}
    >
      {/* Gradient background */}
      <View style={{
        ...Platform.select({
          web: {
            background: isDark
              ? 'linear-gradient(135deg, rgba(13,92,71,0.12) 0%, rgba(23,138,104,0.08) 50%, rgba(13,92,71,0.06) 100%)'
              : 'linear-gradient(135deg, rgba(13,92,71,0.08) 0%, rgba(23,138,104,0.04) 50%, rgba(13,92,71,0.03) 100%)',
          },
        }),
        backgroundColor: isDark ? 'rgba(13,92,71,0.08)' : 'rgba(13,92,71,0.05)',
        padding: isXS ? 24 : 32,
        alignItems: 'center',
      }}>
        {/* Icon */}
        <View style={{
          width: 52, height: 52, borderRadius: 16,
          backgroundColor: '#0D5C4720',
          alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <Icon name="rocket" size={24} color="#0D5C47" solid />
        </View>

        {/* Headline */}
        <Text
          weight="bold"
          style={{
            fontSize: isXS ? 20 : 24,
            textAlign: 'center',
            marginBottom: 8,
            letterSpacing: -0.5,
          }}
        >
          Ready to practice?
        </Text>

        {/* Subtext */}
        <Text
          size="m"
          color="secondary"
          style={{
            textAlign: 'center',
            lineHeight: isXS ? 24 : 26,
            maxWidth: 440,
            marginBottom: 24,
          }}
        >
          {lessonTopic
            ? `Put what you've learned about ${lessonTopic} into action. Sign up for free and start creating with Gab AI.`
            : "Put what you've learned into action. Sign up for free and start creating with Gab AI — no credit card required."}
        </Text>

        {/* CTA Button */}
        <Pressable
          onPress={() => router.push('/signup')}
          className="learn-cta-glow"
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            paddingHorizontal: isXS ? 24 : 32,
            paddingVertical: isXS ? 14 : 16,
            borderRadius: 9999,
            backgroundColor: hovered ? '#0A3F32' : '#0D5C47',
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
          })}
        >
          <Text size="m" weight="semibold" style={{ color: '#fff' }}>
            Start Creating for Free
          </Text>
          <Icon name="arrow-right" size={14} color="#fff" />
        </Pressable>

        {/* Trust signals */}
        <View style={{ flexDirection: 'row', gap: isXS ? 16 : 24, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { icon: 'credit-card', text: 'No credit card' },
            { icon: 'bolt', text: 'Free credits included' },
            { icon: 'clock', text: 'Ready in 30 seconds' },
          ].map((item, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Icon name={item.icon} size={12} color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'} />
              <Text size="xs" color="tertiary">{item.text}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};
